"use client";

import { useEffect, useState } from "react";
import { useRouter, usePathname } from "next/navigation";
import { apiGet, ApiError } from "@/lib/api-client";

export interface CurrentUser {
  userId: string;
  email: string;
  name: string | null;
  emailVerified: boolean;
  authProvider: string;
  avatarUrl: string | null;
  webflowConnected: boolean;
  currentTier: string;
  stripeCustomerId: string | null;
  subscription: {
    status: string;
    planTier: string | null;
    currentPeriodEnd: string | null;
    cancelAtPeriodEnd: boolean | null;
    trialEnd: string | null;
    isOnTrial: boolean;
    daysRemainingOnTrial: number | null;
  } | null;
  usage: {
    sitesUsed: number;
    maxSites: number | null;
    maxItems: number | null;
  };
}

interface UseAuthOptions {
  skipRedirect?: boolean;
}

interface UseAuthResult {
  user: CurrentUser | null;
  loading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useAuth(options: UseAuthOptions = {}): UseAuthResult {
  const { skipRedirect = false } = options;
  const router = useRouter();
  const pathname = usePathname();
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    console.log("[useAuth] fetching /api/me, pathname:", pathname);
    apiGet<CurrentUser>("/api/me")
      .then((data) => {
        if (!cancelled) {
          console.log("[useAuth] /api/me OK — user:", data.email, "tier:", data.currentTier);
          setUser(data);
          setLoading(false);
        }
      })
      .catch((err) => {
        if (cancelled) return;
        console.error("[useAuth] /api/me error — status:", (err as ApiError).status, "message:", (err as Error).message, "pathname:", pathname);
        if (err instanceof ApiError && err.status === 401) {
          if (!skipRedirect) {
            const redirectParam = pathname
              ? `?redirect=${encodeURIComponent(pathname)}`
              : "";
            console.log("[useAuth] 401 — redirecting to /login" + redirectParam);
            router.replace(`/login${redirectParam}`);
          } else {
            setUser(null);
            setLoading(false);
          }
          return;
        }
        setError(err.message ?? "Failed to load session.");
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tick, router, pathname, skipRedirect]);

  return { user, loading, error, refresh: () => setTick((t) => t + 1) };
}
